import { AppDispatch } from '@store/index'
import { incrementPending, setPendingCount } from '@store/slices/offlineSlice'
import { addNotification } from '@store/slices/uiSlice'
import {
  enqueueOperation,
  putRecord,
  deleteRecord,
  getQueueItems,
  STORES,
  type OfflineQueueItem,
} from '@/lib/db/indexed-db'
import { Cigar } from '@store/api/cigarApi'
import { SmokingSession } from '@store/api/sessionApi'

export function generateOfflineEntityId(): string {
  const timestamp = Date.now().toString(36)
  const random = Math.random().toString(36).slice(2, 9)
  return `offline_${timestamp}_${random}`
}

export async function hydratePendingCount(dispatch: AppDispatch): Promise<void> {
  const items = await getQueueItems()
  dispatch(setPendingCount(items.length))
}

async function queueWrite(
  dispatch: AppDispatch,
  operation: OfflineQueueItem['operation'],
  entity: OfflineQueueItem['entity'],
  payload: unknown,
): Promise<void> {
  await enqueueOperation(operation, entity, payload)
  dispatch(incrementPending())
}

export async function offlineCreateCigar(dispatch: AppDispatch, payload: Record<string, unknown>, cigar: Cigar): Promise<Cigar> {
  const id = generateOfflineEntityId()
  const record: Cigar = { ...cigar, ...payload, id } as Cigar
  await putRecord(STORES.CIGARS, record)
  await queueWrite(dispatch, 'CREATE', 'cigar', { ...payload, offlineId: id })
  return record
}

export async function offlineUpdateCigar(
  dispatch: AppDispatch,
  id: string,
  updates: Record<string, unknown>,
  merged: Cigar,
): Promise<void> {
  await putRecord(STORES.CIGARS, merged)
  await queueWrite(dispatch, 'UPDATE', 'cigar', { id, ...updates })
}

export async function offlineDeleteCigar(dispatch: AppDispatch, id: string): Promise<void> {
  await deleteRecord(STORES.CIGARS, id)
  await queueWrite(dispatch, 'DELETE', 'cigar', { id })
}

export async function offlineCreateSession(
  dispatch: AppDispatch,
  payload: Record<string, unknown>,
  session: SmokingSession,
): Promise<SmokingSession> {
  const id = generateOfflineEntityId()
  const record = { ...session, id }
  await putRecord(STORES.SESSIONS, record)
  await queueWrite(dispatch, 'CREATE', 'session', { ...payload, offlineId: id })
  return record
}

export async function offlineUpdateSession(
  dispatch: AppDispatch,
  id: string,
  updates: Record<string, unknown>,
  merged: SmokingSession,
): Promise<void> {
  await putRecord(STORES.SESSIONS, merged)
  await queueWrite(dispatch, 'UPDATE', 'session', { id, ...updates })
}

export async function offlineDeleteSession(dispatch: AppDispatch, id: string): Promise<void> {
  await deleteRecord(STORES.SESSIONS, id)
  await queueWrite(dispatch, 'DELETE', 'session', { id })
}

export function notifyDroppedQueueItems(dispatch: AppDispatch, count: number): void {
  if (count <= 0) return
  const msg = `${count} change${count !== 1 ? 's' : ''} could not be synced after multiple retries and were removed from the queue.`
  dispatch(addNotification({ type: 'error', title: 'Sync failed permanently', message: msg }))
}
